import { Injectable } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { environment } from '@environments/environment';
import { filter } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ApplicationInsightsService {
  private readonly appInsights: ApplicationInsights;

  constructor(private readonly router: Router) {
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: environment.applicationInsightsKey,
        enableAutoRouteTracking: false
      }
    });

    if (environment.applicationInsightsKey) {
      this.appInsights.loadAppInsights();
      this.trackPageViews();
    }
  }

  trackException(error: Error, severityLevel?: number, properties?: Record<string, string>): void {
    this.appInsights.trackException({ exception: error, severityLevel }, properties);
  }

  trackEvent(name: string, properties?: Record<string, string>): void {
    this.appInsights.trackEvent({ name }, properties);
  }

  // Uses the OID claim (localAccountId) as the user identifier — not PII
  setUser(localAccountId: string, accountId?: string): void {
    if (environment.applicationInsightsKey) {
      this.appInsights.setAuthenticatedUserContext(localAccountId, accountId, true);
    }
  }

  clearUser(): void {
    if (environment.applicationInsightsKey) {
      this.appInsights.clearAuthenticatedUserContext();
    }
  }

  private trackPageViews(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd | NavigationError | NavigationCancel =>
            event instanceof NavigationEnd || event instanceof NavigationError || event instanceof NavigationCancel
        )
      )
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.appInsights.trackPageView({ uri: event.urlAfterRedirects });
        } else if (event instanceof NavigationError) {
          const err = event.error instanceof Error ? event.error : new Error(String(event.error));
          this.appInsights.trackException({ exception: err }, { url: event.url });
        } else {
          this.appInsights.trackEvent({ name: 'NavigationCancelled' }, { url: event.url, reason: event.reason ?? '' });
        }
      });
  }
}
